import {
  AlertDialog,
  Checkbox,
  Dialog,
  FileUpload,
  Icon,
  InputDropdown,
  SpinningLoader,
  Text,
  useToast,
} from '@vallourec-ui/vallourec-ui'
import { useTranslation } from 'react-i18next'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { isAxiosError } from 'axios'

import { setWeekYear } from 'date-fns'

import {
  ComponentsColumn,
  ComponentsSearchColumn,
  HeaderContainer,
  Row,
  StyledText,
} from './style'

import { AttachmentProps } from '@/@types/attachment'
import { TypePermissionsReport } from '@/@types/enums'
import { TestPermissionsReport } from '@/@types/global'
import { deleteAttachmentOnDatabase } from '@/api/delete'
import { getFile, getAttachment } from '@/api/get'
import { createAttachmentOnDatabase } from '@/api/post'
import {
  getSignedUrlToPutFile,
  updateUser as updateUserPut,
  uploadFileToBucket,
} from '@/api/put'
import { StyledButton } from '@/components/Header/style'
import { ButtonsContainer } from '@/components/ImageCard/style'
import { LoadingContainer } from '@/components/Loading/style'
import TextFieldInput from '@/components/TextFieldInput'
import { SpinningContainer } from '@/components/UploadBox/styles'
import UploadPreview from '@/components/UploadPreview'
import { useApp } from '@/stores/AppStore'
import {
  DEFAULT_UPLOAD_FILE_TYPES,
  DOCUMENT_UPLOAD_CALLS,
  MAX_PROGRESS_INTERVAL,
  TEST_TYPES,
  TOTAL_PROGRESS,
  YES_NO_SELECT,
} from '@/utils/constants'
import {
  bytesToMb,
  formatAttachmentName,
  generateRandomProgress,
  getFileExtension,
} from '@/utils/utils'

const UserModal: React.FC<{
  isOpen: boolean
  title: string
  userData: UserProps & { fullname?: string; isAdminView?: string }
  setIsOpen: (isOpen: boolean) => void
  onOutsideClick: () => void
  onSave: (user: UserProps) => void
}> = ({ isOpen, userData, setIsOpen, title, onOutsideClick, onSave }) => {
  const initUserTestPermissions = {
    mechanical: [],
    metallographic: [],
    corrosion: [],
    chemical: [],
    ndt: [],
    collapse: [],
  }

  const initialUploadedImages: AttachmentProps[] = Array.from(
    { length: 1 },
    (_, index) => ({
      id: index + 1,
      index,
      path: '',
      file: '',
      extension: '',
      isDocument: false,
      faultAnalysisId: 0,
      name: '',
      size: '',
    })
  )

  const initUploadedImages = (item: AttachmentProps) => {
    setUploadedImages(initialUploadedImages)
    setUploadedImages((prev) => {
      const updatedList = [...prev]
      updatedList[item.index || 0] = {
        ...updatedList[item.index || 0],
        id: item.id,
        idUser: item.idUser,
        name: item?.name,
        size: item?.size,
        path: item?.path,
        position: item?.position,
        extension: item?.name?.split('.').pop() ?? '',
        file: item?.file,
        index: item.index,
      }
      return updatedList
    })
  }

  const { toast } = useToast()
  const { t } = useTranslation()
  const { isLoading, setIsLoading, user } = useApp()
  const [userTestPermissions, setUserTestPermissions] =
    useState<TestPermissionsReport>(initUserTestPermissions)
  const [userPn, setUserPn] = useState<string>('')
  const [userEmail, setUserEmail] = useState<string>('')
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [userPosition, setUserPosition] = useState<string>('')
  const [userDomain, setUserDomain] = useState<string>('')
  const [userIsAdmin, setUserIsAdmin] = useState<boolean | undefined>(undefined)
  const [userIsEnabled, setUserIsEnabled] = useState<boolean | undefined>(
    undefined
  )
  const [uploadingFiles, setUploadingFiles] = useState({} as AttachmentProps)
  const [isImageDeletionOpen, setIsImageDeletionOpen] = useState(false)
  const [uploadedImages, setUploadedImages] = useState(initialUploadedImages)
  const [progress, setProgress] = useState<number>(0)
  const [keyItemTestDiv, setkeyItemTestDiv] = useState(0)
  const removeRequired = (text: string): string => {
    return t<string>(text).replace('*', '')
  }

  const removeEmptyValue = (text: string): string => {
    return text === '-' ? '' : text
  }

  const emailIsValid = (email: string): boolean => {
    const regex =
      // eslint-disable-next-line no-useless-escape
      /^[a-z0-9][a-z0-9-_\.]+@([a-z]|[a-z0-9]?[a-z0-9-]+[a-z0-9])\.[a-z0-9]{2,10}(?:\.[a-z]{2,10})?$/
    return regex.test(email)
  }

  const checkFields = (...fields: (string | boolean | undefined)[]) => {
    return fields.some((item) => item === undefined || item === '')
  }

  const setImagesAfterUpload = async (item: AttachmentProps) => {
    setUploadedImages((prev) => {
      const updatedList = [...prev]
      updatedList[item.index || 0] = {
        ...updatedList[item.index || 0],
        id: item.id,
        name: item?.name,
        size: item?.size,
        path: item?.path,
        extension: item?.name?.split('.').pop() ?? '',
        file: item?.file,
      }
      return updatedList
    })
  }

  const clearModal = () => {
    setUserPn('')
    setUserEmail('')
    setFirstName('')
    setLastName('')
    setUserDomain('')
    setUserPosition('')
    setUserIsAdmin(undefined)
    setUserIsEnabled(undefined)
    setUserTestPermissions(initUserTestPermissions)
  }

  useEffect(() => {
    if (isOpen && userData) {
      setIsLoading(true)
      const {
        pn,
        email,
        name,
        domain,
        administrator,
        active,
        permissions,
        position,
      } = userData

      setUserPn(removeEmptyValue(pn))
      setUserEmail(email)
      setFirstName(name)
      setLastName('')
      setUserPosition(position)
      setUserDomain(removeEmptyValue(domain))
      setUserIsAdmin(administrator)
      setUserIsEnabled(active)
      setUserTestPermissions({
        ...initUserTestPermissions,
        ...(permissions ?? {}),
      })
      setUploadedImages(initialUploadedImages)

      getAttachment({
        idUser: userData.id,
      })
        .then((data) => {
          if (data) {
            initUploadedImages(data)
          }
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      clearModal()
    }
  }, [isOpen])

  const handleOnSave = async () => {
    const hasError = checkFields(firstName)
    const hasEmailError = !emailIsValid(userEmail)

    if (hasError || hasEmailError) {
      return toast({
        title: t<string>('users.userModal.fields.error.warning'),
        description: `${t<string>(
          `users.userModal.fields.error.${hasEmailError ? 'invalidEmail' : 'mandatoryFieldsMessage'}`
        )}.${hasEmailError ? '' : ' (*)'}`,
        type: 'warn',
      })
    }

    let success = false

    try {
      setIsLoading(true)

      const userSave = {
        user: {
          id: userData.id,
          pn: userPn,
          email: userEmail,
          domain: userDomain,
          isAdmin: userIsAdmin,
          isEnabled: userIsEnabled,
          name: firstName,
          lastName,
          position: userPosition,
          permissions: userTestPermissions,
        },
      }
      await updateUserPut(userSave)

      success = true

      toast({
        type: 'info',
        title: t<string>('users.userModal.fields.alertMessages.updating'),
      })

      onSave({
        ...userSave.user,
        administrator: userSave.user.isAdmin,
        active: userSave.user.isEnabled,
      })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
      if (isAxiosError(error)) {
        const { message: errorMessage } = error.response?.data || {}
        toast({
          title: 'Error',
          description:
            errorMessage || (error?.message ?? t<string>('home.error')),
          type: 'error',
        })
      }
    } finally {
      if (success) {
        onOutsideClick()
        clearModal()
      }
      setIsLoading(false)
    }
  }

  const disabledEmail = useMemo(
    () => emailIsValid(userData.email),
    [userData.email]
  )

  const updateProgress = (progressValue: number) => {
    setUploadingFiles((prev: AttachmentProps) => {
      return {
        ...prev,
        progress: Number(prev.progress) + progressValue,
      }
    })
  }

  const dealWithFile = (file: File) => {
    // // const data = addNewFile(file)
    // // const reader = new FileReader()
    // // reader.readAsArrayBuffer(file)
    // // reader.onload = async (e: ProgressEvent<FileReader>) => {
    // // get random progress values
    const progressValues = generateRandomProgress(
      TOTAL_PROGRESS,
      DOCUMENT_UPLOAD_CALLS,
      MAX_PROGRESS_INTERVAL
    )

    // console.log(progressValues)

    // // get presigned url to upload
    // setProgress((prevProgress) => prevProgress + progressValues[0])
    // // const url = await getSignedUrlToPutFile(data.path)
    // // upload file to S3 Bucket
    // setProgress((prevProgress) => prevProgress + progressValues[1])
    // // await uploadFileToBucket(String(url), e.target.result)
    // // create attachment on database
    // setProgress((prevProgress) => prevProgress + progressValues[2])
    // // await createAttachmentOnDatabase(data)
    // // get updated list from database
    // setProgress((prevProgress) => prevProgress + progressValues[3])
    // // const response = await getAttachments({
    // //   analysisId: data.faultAnalysisId,
    // //   isDocument: data.isDocument,
    // // })
    // // if (response) {
    // // setUploadedFiles(response)
    // setProgress((prevProgress) => prevProgress + progressValues[4])
    // // setUploadingFiles((prev) => {
    // //   prev = prev.filter((item) => item.progress !== 100)
    // //   return prev
    // // })
    // // }
    // // }
    // // }

    const reader = new FileReader()
    reader.readAsArrayBuffer(file)
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const formattedName = formatAttachmentName(file.name)
      const data = {
        idUser: userData.id,
        extension: getFileExtension(file.name),
        path: `signature/${userData.id}/${formattedName}`,
        size: `${bytesToMb(file.size)} MB`,
        name: formattedName,
        index: 0,
      }
      const url = await getSignedUrlToPutFile(data.path) // get presigned url to upload
      await uploadFileToBucket(String(url), e.target.result) // upload file to S3 Bucket
      await createAttachmentOnDatabase(data) // create attachment on database

      const response = await getAttachment({
        idUser: userData.id,
      })

      if (response) {
        setUploadedImages([response])
        updateProgress(progressValues[4])
        setUploadingFiles((prev) => {
          prev = prev.progress !== 100
          return prev
        })
      }
    }
  }

  const selectedTypes = {
    'image/jpeg': DEFAULT_UPLOAD_FILE_TYPES['image/jpeg'],
    'image/png': DEFAULT_UPLOAD_FILE_TYPES['image/png'],
  }

  const setData = (file: File) => {
    try {
      if (file) {
        dealWithFile(file)
      }
    } catch (error) {
      if (isAxiosError(error)) {
        toast({
          title: t<string>('warning'),
          description: error.message,
          type: 'warn',
        })
      }
    }
  }

  const setCheckBoxPermissions = (testType: string, permission: string) => {
    let testSPermissions = userTestPermissions
    if (!testSPermissions) {
      testSPermissions = {
        mechanical: [],
        metallographic: [],
        corrosion: [],
        chemical: [],
        ndt: [],
        collapse: [],
      }
    }
    if (!testSPermissions[testType]) {
      testSPermissions[testType] = []
    }

    if (testSPermissions[testType].includes(permission)) {
      testSPermissions[testType] = testSPermissions[testType].filter(
        (item) => item !== permission
      )
    } else {
      testSPermissions[testType].push(permission)
    }

    setUserTestPermissions(testSPermissions)
    setkeyItemTestDiv((prev) => prev + 1)
  }

  const onDownload = async (path: string, filename: string) => {
    setIsLoading(true)
    const url = await getFile(path)
    const a = document.createElement('a')
    a.download = filename
    a.href = url
    a.click()
    setIsLoading(false)
  }

  const onDelete = (id: number | undefined) => {
    if (id) {
      setIsImageDeletionOpen(true)
    }
  }

  const applyDelete = async () => {
    try {
      setIsLoading(true)
      setIsImageDeletionOpen(false)
      if (uploadedImages[0].id) {
        await deleteAttachmentOnDatabase(uploadedImages[0].id)
        setUploadedImages(initialUploadedImages)
      }
    } catch (error) {
      if (isAxiosError(error)) {
        toast({ title: 'Error', description: error.message, type: 'error' })
      }
      setIsImageDeletionOpen(false)
    } finally {
      setIsLoading(false)
    }
  }

  const onOutsideClickAlert = () => {
    setIsImageDeletionOpen(false)
  }

  return (
    <Dialog.Root
      isOpen={isOpen}
      setIsOpen={() => setIsOpen(false)}
      closeOnInteractOutside={false}
      width="60rem"
      header={
        <Dialog.Header title={title} />
      }
      footer={
        <Dialog.Footer
          buttonText={t('users.userModal.confirmButtonLabel')}
          onConfirm={handleOnSave}
          onClose={() => {
            onOutsideClick()
            clearModal()
          }}
        />
      }
    >
      <HeaderContainer>
        <Text size="text-lg" weight="semi-bold" color="neutral-color-80">
          {t<string>('users.userModal.title')}
        </Text>
        <Row>
          <ComponentsColumn>
            <StyledText>
              {removeRequired(t<string>('users.userModal.fields.pn'))}
            </StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="userPn"
              disabled={!(user?.isAdmin || user?.email === userData.email)}
              onChange={(event) => setUserPn(event.target.value)}
              placeholder={removeRequired('users.userModal.fields.pn')}
              width="100%"
              value={userPn}
              maxLength={10}
            />
          </ComponentsColumn>
          <ComponentsColumn>
            <StyledText>{t<string>('users.userModal.fields.email')}</StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="userEmail"
              onChange={(event) => setUserEmail(event.target.value)}
              placeholder={removeRequired('users.userModal.fields.email')}
              width="100%"
              value={userEmail}
              disabled={
                disabledEmail ||
                !(user?.isAdmin || user?.email === userData.email)
              }
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>
              {t<string>('users.userModal.fields.firstName')}
            </StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="userFirstName"
              disabled={false}
              onChange={(event) => setFirstName(event.target.value)}
              placeholder={removeRequired('users.userModal.fields.firstName')}
              width="100%"
              value={firstName}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          <ComponentsColumn>
            <StyledText>
              {t<string>('users.userModal.fields.position')}
            </StyledText>
            <TextFieldInput
              fontSize="text-sm"
              name="userPosition"
              disabled={!(user?.isAdmin || user?.email === userData.email)}
              onChange={(event) => setUserPosition(event.target.value)}
              placeholder={removeRequired('users.userModal.fields.position')}
              width="100%"
              value={userPosition}
            />
          </ComponentsColumn>
        </Row>
        <Row>
          {user?.isAdmin && (
            <>
              <ComponentsSearchColumn>
                <Checkbox
                  withLabel
                  label={t<string>('users.userModal.fields.isAdmin')}
                  checked={userIsAdmin}
                  onCheckedChange={(value) => setUserIsAdmin(value)}
                  withIcon={false}
                  iconName=""
                  iconColor=""
                  labelColor=""
                />
              </ComponentsSearchColumn>
              <ComponentsSearchColumn>
                <Checkbox
                  withLabel
                  label={t<string>('users.userModal.fields.isEnabled')}
                  checked={userIsEnabled}
                  onCheckedChange={(value) => setUserIsEnabled(value)}
                  withIcon={false}
                  iconName=""
                  iconColor=""
                  labelColor=""
                />
              </ComponentsSearchColumn>
            </>
          )}
        </Row>
        {(user?.isAdmin || user?.email === userData.email) && (
          <Row>
            <ComponentsSearchColumn isLast>
              <StyledText>Assinatura</StyledText>
              <UploadPreview
                key="upload-preview-signature"
                uploadText={t('user.signature.uploadContainer.uploadText')}
                dragAndDropText={t(
                  'user.signature.uploadContainer.dragAndDropText'
                )}
                setData={setData}
                onDownload={() =>
                  onDownload(uploadedImages[0].path, uploadedImages[0]?.name)
                }
                onDelete={() => onDelete(uploadedImages[0].id)}
                item={uploadedImages[0] ?? {}}
                acceptedTypes={selectedTypes}
              />
              {/* <FileUpload
                filesAcceptedLabel="Accepted files"
                disabled={user?.email != userEmail}
                accept=".jpg, .jpeg, .png, .bmp, .gif, .tiff, .tif, .heic"
                handleOnDrop={(file) => setData(file)}
                handleDeleteFile={(file) => console.log('File deleted:', file)}
                progress={progress}
              /> */}
            </ComponentsSearchColumn>
          </Row>
        )}
      </HeaderContainer>

      {user?.isAdmin && (
        <HeaderContainer>
          <Row>
            <ComponentsSearchColumn
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                gap: '0.5rem',
              }}
              key={keyItemTestDiv}
            >
              <Text size="text-lg" weight="semi-bold" color="neutral-color-80">
                {t<string>('users.userModal.fields.testPermissions')}
              </Text>
              {TEST_TYPES.map((item) => (
                <Row
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'left',
                  }}
                  key={item}
                >
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div>
                      <Text
                        size="text-md"
                        weight="semi-bold"
                        color="neutral-color-80"
                      >
                        {t(`testTypes.${item}`)}
                      </Text>
                    </div>
                    <div style={{ display: 'flex', gap: '4rem' }}>
                      <Text size="text-sm" color="neutral-color-80">
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          {t<string>('users.userModal.fields.elaborate')}
                          <Checkbox
                            checked={
                              userTestPermissions
                                ? userTestPermissions[`${item}`].includes(
                                    TypePermissionsReport.ELABORATION
                                  )
                                : false
                            }
                            onCheckedChange={() =>
                              setCheckBoxPermissions(
                                item,
                                TypePermissionsReport.ELABORATION
                              )
                            }
                            label=""
                          />
                        </div>
                      </Text>
                      {item === 'corrosion' && (
                        <Text size="text-sm" color="neutral-color-80">
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            {t<string>('users.userModal.fields.checker')}
                            <Checkbox
                              checked={
                                userTestPermissions
                                  ? userTestPermissions[`${item}`].includes(
                                      TypePermissionsReport.CHECK
                                    )
                                  : false
                              }
                              onCheckedChange={() =>
                                setCheckBoxPermissions(
                                  item,
                                  TypePermissionsReport.CHECK
                                )
                              }
                            />
                          </div>
                        </Text>
                      )}
                      <Text size="text-sm" color="neutral-color-80">
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          {t<string>('users.userModal.fields.approver')}
                          <Checkbox
                            checked={
                              userTestPermissions
                                ? userTestPermissions[`${item}`].includes(
                                    TypePermissionsReport.APPROVE
                                  )
                                : false
                            }
                            onCheckedChange={() =>
                              setCheckBoxPermissions(
                                item,
                                TypePermissionsReport.APPROVE
                              )
                            }
                          />
                        </div>
                      </Text>
                      <Text size="text-sm" color="neutral-color-80">
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          {t<string>('users.userModal.fields.viewer')}
                          <Checkbox
                            checked={
                              userTestPermissions
                                ? userTestPermissions[`${item}`].includes(
                                    TypePermissionsReport.VIEW
                                  )
                                : false
                            }
                            onCheckedChange={() =>
                              setCheckBoxPermissions(
                                item,
                                TypePermissionsReport.VIEW
                              )
                            }
                          />
                        </div>
                      </Text>
                    </div>
                  </div>
                </Row>
              ))}
            </ComponentsSearchColumn>
          </Row>
        </HeaderContainer>
      )}

      <AlertDialog
        open={isImageDeletionOpen}
        variant="error"
        title={`${t<string>('user.signature.deletionAlert.title')}`}
        description={t<string>('user.signature.deletionAlert.description')}
        onOpenChange={onOutsideClickAlert}
        close={onOutsideClickAlert}
        footer={
          <ButtonsContainer>
            <StyledButton
              onClick={onOutsideClickAlert}
              key="cancel-button"
              variant="errorWithBorders"
            >
              <Text>{t<string>('user.signature.deletionAlert.cancel')}</Text>
            </StyledButton>
            <StyledButton
              onClick={applyDelete}
              key="confirm-button"
              variant="errorWithoutBorders"
            >
              <Icon name="DeleteBinLine" color="feedback-negative-0" />
              <Text>{t<string>('user.signature.deletionAlert.delete')}</Text>
            </StyledButton>
          </ButtonsContainer>
        }
      />
    </Dialog.Root>
  )
}

export default UserModal
